import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useForm, SubmitHandler } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelfServiceRequestMagicLink } from '@/api/trafikportalen';
import { useBranding } from '@/hooks/useBranding';
import { useFavicon } from '@/hooks/useFavicon';
import { useTenantID } from '@/hooks/useTenantID';
import { Section } from '@/components/Section';
import { FormElement } from '@/components/FormElement';
import { InputText } from '@/components/Inputs/InputText';
import Button from '@/components/core/Button';
import { IconChevronLeft, IconCheckCircle } from '@/icons';
import { cn } from '@/helpers/classnames';
import { DocumentHead } from '@/components/core/DocumentHead';

const RESEND_COOLDOWN_SECONDS = 60;

export const Route = createFileRoute('/$tenantId/self-service/login')({
  component: SelfServiceLogin,
});

type EmailFormData = { email: string };

function SelfServiceLogin() {
  const tenantId = useTenantID();
  const { formatMessage } = useIntl();
  useFavicon(tenantId);

  const emailSchema = useMemo(
    () =>
      z.object({
        email: z
          .string()
          .min(1, formatMessage({ defaultMessage: 'E-postadress krävs' }))
          .email(
            formatMessage({
              id: 'error.invalid_email',
              defaultMessage: 'Ogiltig e-postadress',
            }),
          ),
      }),
    [formatMessage],
  );
  const branding = useBranding();
  const [submitted, setSubmitted] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const cooldownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const requestMagicLink = useSelfServiceRequestMagicLink();

  const { handleSubmit, control, formState } = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
    mode: 'onBlur',
    defaultValues: { email: '' },
  });

  const startCooldown = useCallback(() => {
    setCooldownSeconds(RESEND_COOLDOWN_SECONDS);

    if (cooldownTimerRef.current) {
      clearInterval(cooldownTimerRef.current);
    }

    cooldownTimerRef.current = setInterval(() => {
      setCooldownSeconds((prev) => {
        if (prev <= 1) {
          if (cooldownTimerRef.current) {
            clearInterval(cooldownTimerRef.current);
            cooldownTimerRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
      }
    };
  }, []);

  const onSubmit: SubmitHandler<EmailFormData> = (data) => {
    requestMagicLink.mutate(
      { data: { email: data.email, tenant_id: tenantId } },
      {
        onSuccess: () => {
          setSubmitted(true);
          startCooldown();
        },
      },
    );
  };

  const handleResend = useCallback(() => {
    setSubmitted(false);
  }, []);

  return (
    <>
      <DocumentHead
        titleKey="page.selfService.login.title"
        descriptionKey="page.selfService.login.description"
      />
      <div
        className={cn('flex grow items-center justify-center p-5', tenantId)}
      >
        <Section className="m-auto w-full max-w-md flex-col gap-6 bg-background-100">
          <div className="mx-auto h-[80px] w-full max-w-[200px] overflow-hidden">
            {branding?.logo}
          </div>

          <h1 className="text-center text-2xl font-medium text-text-950">
            <FormattedMessage defaultMessage="Självbetjäning" />
          </h1>

          {submitted ? (
            <div
              className="flex flex-col items-center gap-4 text-center"
              role="status"
            >
              <div className="rounded-full bg-green-500 p-4">
                <IconCheckCircle
                  className="size-8 text-contrast-green"
                  aria-hidden="true"
                />
              </div>
              <p className="text-text-700">
                <FormattedMessage defaultMessage="Vi har skickat en inloggningslänk till din e-post. Klicka på länken för att logga in." />
              </p>
              <Button
                variant="ghost"
                onClick={handleResend}
                aria-disabled={cooldownSeconds > 0}
                disabled={cooldownSeconds > 0}
              >
                {cooldownSeconds > 0 ? (
                  <FormattedMessage
                    defaultMessage="Skicka igen ({seconds}s)"
                    values={{ seconds: cooldownSeconds }}
                  />
                ) : (
                  <FormattedMessage defaultMessage="Skicka igen" />
                )}
              </Button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col gap-4"
            >
              <p className="text-center text-text-700">
                <FormattedMessage defaultMessage="Ange din e-postadress så skickar vi en unik inloggningslänk." />
              </p>
              <FormElement
                input={
                  <InputText
                    label={formatMessage({ defaultMessage: 'E-postadress' })}
                    name="email"
                    control={control}
                    editMode={true}
                    error={formState.errors.email}
                    placeholder="exempel@epost.se"
                    helperTextReserveSpace
                  />
                }
              />
              <Button
                type="submit"
                variant="filled"
                size="lg"
                className="w-full"
                loading={requestMagicLink.isPending}
              >
                <FormattedMessage defaultMessage="Skicka inloggningslänk" />
              </Button>
            </form>
          )}
          <Button
            to="/$tenantId/login"
            params={{ tenantId }}
            variant="ghost"
            startIcon={<IconChevronLeft aria-hidden="true" />}
            className="mx-auto"
          >
            <FormattedMessage defaultMessage="Tillbaka till inloggning" />
          </Button>
        </Section>
      </div>
    </>
  );
}
